import {
	Box,
	Drawer,
	DrawerBody,
	DrawerCloseButton,
	DrawerContent,
	DrawerOverlay,
	Flex,
	HStack,
	Icon,
	IconButton,
	Image,
	Link,
	useColorModeValue,
	useDisclosure,
} from '@chakra-ui/react';
import { IconMenu2 } from '@tabler/icons';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { Scrollbars } from 'react-custom-scrollbars-2';
import { IoMenuOutline } from 'react-icons/io5';

import logoEasyBike from '../../assets/images/logos/logo-easy360-bike-soft-red.png';
import logoEasyMenu from '../../assets/images/logos/logo-easy360-red-and-blue.svg';
import { renderThumb, renderTrack, renderView } from '../scrollbar/Scrollbar';
import { HSeparator } from '../separator/Separator';
import Content from './components/Content';

export function Sidebar(props) {
	const { routes, sidebarState, setSidebarState, sidebarHelpState } = props;

	const sidebarBgColor = useColorModeValue('easyBLUE.100', 'navy.800');
	const buttonBgColor = useColorModeValue('easyBrandMenu.100', 'navy.800');
	const buttonColor = useColorModeValue('easyBLUE.300', 'white');
	const buttonBorderColor = useColorModeValue(
		'easyBrandMenu.200',
		'navy.800'
	);
	const sidebarRadius = '30px';
	const sidebarMargins = '0px';

	const variantChange = '0.2s linear';
	const shadow = useColorModeValue(
		'14px 17px 40px 4px rgba(112, 144, 176, 0.08)',
		'unset'
	);

	useEffect(() => {
		window.onresize = function () {
			const w =
				window.innerWidth ||
				document.documentElement.clientWidth ||
				document.body.clientWidth;
			if (w < 1200) {
				setSidebarState('closed');
			} else {
				setSidebarState('opened');
			}
		};
	}, []);

	return (
		<Box
			position='fixed'
			minH='100%'
		>
			<Box
				onClick={() => {
					if (sidebarState === 'closed') setSidebarState('opened');
				}}
				cursor={sidebarState === 'closed' ? 'pointer' : undefined}
				bg={sidebarBgColor}
				transition={variantChange}
				w={sidebarState === 'opened' ? '290px' : '76px'}
				ms={{
					sm: '16px',
				}}
				my={{
					sm: '16px',
				}}
				h='calc(100vh - 32px)'
				m={sidebarMargins}
				borderRadius={sidebarRadius}
				minH='100%'
				overflowX='hidden'
				boxShadow={shadow}
			>
				<Scrollbars
					renderTrackVertical={renderTrack}
					renderThumbVertical={renderThumb}
					renderView={renderView}
				>
					<Flex
						align='center'
						justifyContent={
							sidebarState === 'opened'
								? 'space-between'
								: 'flex-start'
						}
						pt='15px'
						w='100%'
					>
						<HStack
							mb='auto'
							ps='10px'
							ml={sidebarState === 'opened' ? '10px' : '0px'}
							my='22px'
						>
							<IconButton
								onClick={() => {
									if (sidebarState === 'closed') {
										setSidebarState('opened');
									} else {
										setSidebarState('closed');
									}
								}}
								variant='outline'
								icon={<IconMenu2 />}
								colorScheme={buttonColor}
								borderColor={buttonBorderColor}
								size='sm'
								borderRadius='10px'
								_hover={{ cursor: 'pointer' }}
								bg={
									sidebarState === 'opened'
										? buttonBgColor
										: 'secondaryGray.400'
								}
								mr={'20px'}
								display={
									sidebarState === 'opened' ? 'flex' : 'none'
								}
								aria-label='Toggle sidebar'
							/>
							<IconButton
								onClick={() => {
									if (sidebarState === 'closed') {
										setSidebarState('opened');
									}
								}}
								display={
									sidebarState === 'closed' ? 'flex' : 'none'
								}
								variant='outline'
								icon={
									<Image
										src={logoEasyBike}
										alt='Easy360 Bike'
										w='32px'
										h='32px'
										objectFit='contain'
									/>
								}
								colorScheme={buttonColor}
								borderColor={buttonBorderColor}
								size='sm'
								borderRadius='10px'
								_hover={{ cursor: 'pointer' }}
								bg='secondaryGray.400'
								mr={'20px'}
								aria-label='Open sidebar'
							/>
							<Link href='/'>
								<Image
									src={logoEasyMenu}
									alt='Easy360'
									w='164px'
									h='59px'
									display={
										sidebarState === 'opened'
											? 'flex'
											: 'none'
									}
									transition='all 0.2s ease-in-out'
									objectFit='contain'
								/>
							</Link>
						</HStack>
					</Flex>
					<HSeparator
						mb='20px'
						bg='secondaryGray.400'
					/>
					<Content
						routes={routes}
						sidebarState={sidebarState}
						sidebarHelpState={sidebarHelpState}
					/>
				</Scrollbars>
			</Box>
		</Box>
	);
}

export function SidebarResponsive(props) {
	const sidebarBackgroundColor = useColorModeValue(
		'easyBLUE.100',
		'navy.800'
	);
	const menuColor = useColorModeValue('easyBLUE.300', 'white');
	const { isOpen, onOpen, onClose } = useDisclosure();
	const btnRef = React.useRef();

	const { routes } = props;
	return (
		<Flex
			display={{ sm: 'flex', xl: 'none' }}
			alignItems='center'
		>
			<Flex
				ref={btnRef}
				w='max-content'
				h='max-content'
				onClick={onOpen}
			>
				<Icon
					as={IoMenuOutline}
					color={menuColor}
					my='auto'
					w='20px'
					h='20px'
					me='10px'
					_hover={{ cursor: 'pointer' }}
				/>
			</Flex>

			<Drawer
				isOpen={isOpen}
				onClose={onClose}
				placement={
					document.documentElement.dir === 'rtl' ? 'right' : 'left'
				}
				finalFocusRef={btnRef}
			>
				<DrawerOverlay />
				<DrawerContent
					w='285px'
					maxW='285px'
					ms={{
						sm: '16px',
					}}
					my={{
						sm: '16px',
					}}
					borderRadius='16px'
					bg={sidebarBackgroundColor}
				>
					<DrawerCloseButton
						zIndex='3'
						onClose={onClose}
						_focus={{ boxShadow: 'none' }}
						_hover={{ boxShadow: 'none' }}
					/>
					<DrawerBody
						maxW='285px'
						px='0rem'
						pb='0'
					>
						<Scrollbars
							autoHide
							renderTrackVertical={renderTrack}
							renderThumbVertical={renderThumb}
							renderView={renderView}
						>
							<Content routes={routes} />
						</Scrollbars>
					</DrawerBody>
				</DrawerContent>
			</Drawer>
		</Flex>
	);
}

Sidebar.propTypes = {
	logoText: PropTypes.string,
	routes: PropTypes.arrayOf(PropTypes.object),
	variant: PropTypes.string,
};

export default Sidebar;
